import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Users as UsersIcon, Shield, Truck, Building2, Search, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagement = () => {
  const { user, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showAddUser, setShowAddUser] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '', role: 'owner', organization_id: '', pin_code: '',
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, orgsRes] = await Promise.all([
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/organizations`, { headers }),
      ]);
      setUsers(usersRes.data);
      setOrgs(orgsRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    if (!form.organization_id && form.role !== 'superadmin') {
      toast.error('Select an organization');
      return;
    }
    setSaving(true);
    try {
      const org = orgs.find(o => o.id === form.organization_id);
      await axios.post(`${API}/users`, {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        organization_id: form.organization_id || null,
        organization_type: org?.type || null,
        pin_code: form.pin_code || null,
      }, { headers });
      toast.success(`User "${form.name}" created`);
      setShowAddUser(false);
      setForm({ name: '', email: '', password: '', role: 'owner', organization_id: '', pin_code: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
    setSaving(false);
  };

  const roleColors = {
    superadmin: 'bg-red-100 text-red-700',
    owner: 'bg-blue-100 text-blue-700',
    shipper: 'bg-orange-100 text-orange-700',
    receiver: 'bg-green-100 text-green-700',
  };

  const roleIcons = {
    superadmin: Shield,
    owner: Building2,
    shipper: Truck,
    receiver: Building2,
  };

  const filtered = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.organization_name?.toLowerCase().includes(q);
    }
    return true;
  });

  const roleCounts = { all: users.length };
  users.forEach(u => { roleCounts[u.role] = (roleCounts[u.role] || 0) + 1; });

  return (
    <div className="space-y-6" data-testid="user-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-slate-500 text-sm">{users.length} users across {orgs.length} organizations</p>
        </div>
        <Button onClick={() => setShowAddUser(true)} data-testid="add-user-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search name, email, org..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="user-search"
          />
        </div>
        <div className="flex gap-1">
          {['all', 'superadmin', 'owner', 'shipper', 'receiver'].map(r => (
            <Button
              key={r}
              variant={roleFilter === r ? 'default' : 'outline'}
              size="sm"
              onClick={() => setRoleFilter(r)}
              className="text-xs"
              data-testid={`filter-${r}`}
            >
              {r === 'all' ? 'All' : r.charAt(0).toUpperCase() + r.slice(1)} ({roleCounts[r] || 0})
            </Button>
          ))}
        </div>
      </div>

      {/* User List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Name</th>
                  <th className="text-left p-3 font-medium text-slate-600">Email</th>
                  <th className="text-left p-3 font-medium text-slate-600">Role</th>
                  <th className="text-left p-3 font-medium text-slate-600">Organization</th>
                  <th className="text-left p-3 font-medium text-slate-600">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400">No users found</td></tr>
                ) : (
                  filtered.map(u => {
                    const RoleIcon = roleIcons[u.role] || UsersIcon;
                    return (
                      <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                              {u.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <span className="font-medium text-slate-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-slate-600 font-mono text-xs">{u.email}</td>
                        <td className="p-3">
                          <Badge className={`text-xs ${roleColors[u.role] || 'bg-slate-100 text-slate-600'}`}>
                            <RoleIcon className="w-3 h-3 mr-1" />{u.role}
                          </Badge>
                        </td>
                        <td className="p-3 text-slate-600 text-xs">{u.organization_name || '—'}</td>
                        <td className="p-3 text-slate-400 text-xs">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>Create a new user account for any organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" placeholder="e.g. Per Hansen" />
            </div>
            <div className="space-y-1">
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" placeholder="e.g. per@company.no" />
            </div>
            <div className="space-y-1">
              <Label>Password *</Label>
              <Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" placeholder="Temporary password" />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="shipper">Shipper</SelectItem>
                  <SelectItem value="receiver">Receiver</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Organization *</Label>
              <Select value={form.organization_id} onValueChange={v => setForm({ ...form, organization_id: v })}>
                <SelectTrigger data-testid="new-user-org">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.filter(o => {
                    if (form.role === 'owner') return o.type === 'pallet_pool';
                    if (form.role === 'shipper') return o.type === 'shipper';
                    if (form.role === 'receiver') return o.type === 'receiver';
                    return true;
                  }).map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name} ({o.type})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.role === 'shipper' && (
              <div className="space-y-1">
                <Label>PIN Code (optional — for mobile login)</Label>
                <Input type="tel" inputMode="numeric" maxLength={6} value={form.pin_code} onChange={e => setForm({ ...form, pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) })} data-testid="new-user-pin" placeholder="4-6 digits" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="create-user-submit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
